/* emoji-render — shared, dependency-free emoji painting with auto-heal.
 *
 * THE PROBLEM IT SOLVES
 *   Emoji are drawn by the platform's native font (Segoe UI Emoji on Windows,
 *   Apple Color Emoji on iOS/macOS, Noto on Android/Linux). That font is NOT
 *   complete on every device:
 *     - Windows' Segoe UI Emoji ships ZERO country flags, on EVERY version, by
 *       design  →  🇺🇸 renders as the two letters "US", 🇷🇺 as "RU", etc.
 *     - Older Segoe / old Android lag the Unicode spec  →  newer emoji render as
 *       ".notdef" tofu boxes (□).
 *   So "just use the native font" looks broken for a big slice of visitors.
 *
 * THE FIX (root cause, not symptom)
 *   Keep the native glyph wherever the device CAN draw it (fast, zero bytes,
 *   the look users expect on their platform) and transparently swap in a vendor
 *   SVG ONLY for the glyphs this exact device can't draw. We decide per device
 *   with a tiny, cached canvas probe instead of a hardcoded list:
 *     - one probe for country flags (🇺🇸): color pixels => flags supported.
 *     - a short ladder of single-codepoint colour faces, one per Unicode-emoji
 *       version => the highest emoji version this font renders in colour.
 *   A real colour-emoji glyph paints chromatic pixels; a tofu box or a
 *   letter-pair flag is monochrome. That separation is razor-sharp (validated
 *   on real Windows: colour ratio 0.74–1.0 vs flags/tofu exactly 0).
 *
 *   ~13 canvas draws, run once, result cached in localStorage keyed by a coarse
 *   platform signature. Never blocks first paint: callers render native
 *   immediately and re-decide once `ready()` resolves (instant on warm cache).
 *
 * PUBLIC API  (window.EmojiRender)
 *   ready()                  -> Promise<caps>   probe once (idle), cache, resolve
 *   capsSync()               -> caps | null     synchronous read (null until ready)
 *   needsImage(em)           -> boolean          does this device need an image for it?
 *   imgUrl(hex, style)       -> string           vendor image url (.svg; .png for fluent3d)
 *   paint(el, em, opts)      -> 'native'|'image' fill an element with the glyph
 *   hexOfChar(char)          -> string           glyph -> emojibase hex (no catalogue needed)
 *   imageFor(em, {px,style}) -> Promise<{img,style,hex}|null>   drawable for a CANVAS
 *   isRegionalFlag(hex)      -> boolean
 *   reprobe()                -> Promise<caps>    force a fresh probe (debug)
 *
 *   em      : { h|hex, e|char, v|version }   (skinned hex/char ok; pass base version)
 *   opts    : { style, char, label, fallbackChar }
 *   style   : 'auto' (default: native + auto-heal) | 'native'
 *             | 'twemoji' | 'noto' | 'openmoji' (flat SVG)
 *             | 'fluent3d' (Microsoft Fluent 3D, PNG — the only glossy pack)
 *             | 'fluent-flat' / 'fluent-hc' (Fluent Flat colour / High Contrast
 *               line art, SVG — /mnemonic-table; own copy, npm mirror behind it)
 *   caps    : { flags:boolean, maxVersion:number, source:string }
 */
(function () {
    'use strict';
    if (window.EmojiRender && !window.EmojiRender._stub) return;

    var CACHE_KEY = 'tb.emoji.caps.v1';
    // Auto-heal packs differ by REASON — verified against the vendored packs:
    //   • Noto has v16/v17 colour glyphs but NO combined country-flag files
    //     (Noto renders flags via font ligatures, so the extracted SVGs are only
    //     single regional-indicator letters).
    //   • Twemoji/OpenMoji have all 258 country flags but are frozen pre-v16/17.
    var HEAL_NEW  = 'noto';      // emoji newer than the installed font
    var HEAL_FLAG = 'twemoji';   // country / subdivision flags
    var PACK_BASE = function () { return window.__EMOJI_PACK_BASE || '/vendor/emoji'; };

    // Per-pack file extension. Three packs are SVG; Fluent is PNG because
    // upstream only ships its "3D" style as 256px raster — that style is the
    // whole point of vendoring it (the flat Fluent styles would add nothing the
    // other three don't already do), so the pack format follows the art.
    var PACK_EXT = { fluent3d: 'png' };

    // OWN COPY FIRST, MIRROR BEHIND IT (05.11.2026, /mnemonic-table).
    // public/vendor/emoji/ is gitignored and every pack is born out of band
    // (scripts/vendor-fluent-emoji.mjs on the RU build box, rsynced to NL). Until
    // that recipe runs on an origin, a new pack is a 404 there — so the two
    // Fluent SVG packs carry a mirror: the same art on npm (@lobehub, MIT, files
    // named by the FULLY-QUALIFIED codepoints — "2744-fe0f", not our "2744";
    // verified 05.11 for 10 glyphs, ACAO *). The mirror is tried only after the
    // own copy failed; once the own pack has failed where the mirror answered,
    // this page stops asking the own copy (no 404 per cell).
    var PACK_MIRROR = {
        'fluent-flat': 'https://cdn.jsdelivr.net/npm/@lobehub/fluent-emoji-flat@1.1.0/assets/{cp}.svg',
        'fluent-hc': 'https://cdn.jsdelivr.net/npm/@lobehub/fluent-emoji-mono@1.1.0/assets/{cp}.svg'
    };
    // A glyph the pack lacks goes to the next pack of the same kind before the heal
    // chain: Fluent has nothing newer than Unicode 15 (🫜 root vegetable is 16), and
    // Noto is the closest flat colour art we serve.
    var PACK_NEXT = { 'fluent-flat': ['noto'] };
    var packDown = {};   // pack → true once the own copy failed and the mirror answered
    // Fluent 3D covers 1595 of our 1940 catalogue rows (82.2%): it lags Unicode
    // (no v16/v17) and omits the couple/handshake ZWJ sequences. So it is an
    // EXPLICIT style only — never an auto-heal pack — and callers asking for it
    // get it appended to, not substituted for, the heal chain, so a gap lands on
    // whatever the device can actually draw instead of on a broken image.

    // Curated probe ladder: ONE single-codepoint, high-chroma face per Unicode
    // emoji version we ship. Faces are always emoji-presentation + colourful, so
    // a supported one paints lots of chroma — no false "monochrome" reads (unlike
    // grey emoji like 💀 or text-default glyphs like ✉). Stable Unicode facts.
    var LADDER = [
        { v: 1,    ch: '😀' }, // 😀 grinning face            (also the canvas-sanity baseline)
        { v: 5,    ch: '🤩' }, // 🤩 star-struck
        { v: 11,   ch: '🥰' }, // 🥰 smiling face with hearts
        { v: 12,   ch: '🥱' }, // 🥱 yawning face
        { v: 12.1, ch: '🧑‍🦰' }, // 🧑‍🦰 person: red hair
        { v: 13,   ch: '🥲' }, // 🥲 smiling face with tear
        { v: 13.1, ch: '😶‍🌫️' }, // 😶‍🌫️ face in clouds
        { v: 14,   ch: '🫠' }, // 🫠 melting face
        { v: 15,   ch: '🫨' }, // 🫨 shaking face
        { v: 15.1, ch: '🙂‍↔️' }, // 🙂‍↔️ head shaking horizontally
        { v: 16,   ch: '🫩' }, // 🫩 face with bags under eyes
        { v: 17,   ch: '🫪' }  // 🫪 distorted face
    ];
    var FLAG_PROBE = '🇺🇸'; // 🇺🇸

    // Country flag = regional-indicator PAIR (1F1xx 1F1xx) or subdivision TAG
    // sequence (1F3F4 + E00xx tags). NOT the pirate flag (1F3F4-200D-…) or the
    // rainbow/black/white/checkered flags — those are ordinary glyphs and follow
    // the version rule, so they must not be gated by the country-flag probe.
    var RE_REGIONAL = /^1F1[E-F][0-9A-F]-1F1[E-F][0-9A-F]$/i;
    var RE_SUBDIV   = /^1F3F4-E00/i;
    function isRegionalFlag(hex) {
        return RE_REGIONAL.test(hex) || RE_SUBDIV.test(hex);
    }

    // ── Canvas chroma probe ───────────────────────────────────────────────────
    var _canvas = null, _ctx = null;
    function probeColour(ch) {
        try {
            if (!_canvas) {
                _canvas = document.createElement('canvas');
                _canvas.width = _canvas.height = 52;
                _ctx = _canvas.getContext('2d', { willReadFrequently: true });
                _ctx.textBaseline = 'top';
                _ctx.font = '36px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
            }
            _ctx.clearRect(0, 0, 52, 52);
            _ctx.fillText(ch, 6, 6);
            var d = _ctx.getImageData(0, 0, 52, 52).data;
            var ink = 0, chroma = 0;
            for (var i = 0; i < d.length; i += 4) {
                if (d[i + 3] > 16) {
                    ink++;
                    if (Math.max(d[i], d[i + 1], d[i + 2]) - Math.min(d[i], d[i + 1], d[i + 2]) > 24) chroma++;
                }
            }
            // Colour glyph: meaningful chromatic coverage. Tofu / letter-pair: ~0.
            return ink > 24 && chroma / ink > 0.05;
        } catch (_) {
            return null; // canvas unavailable / readback poisoned
        }
    }

    // ── Capabilities ──────────────────────────────────────────────────────────
    var caps = null;
    var readyPromise = null;

    function platformSig() {
        var n = navigator;
        var uaFamily = String(n.userAgent || '').replace(/[0-9][0-9.]*/g, '#'); // strip version churn
        var p = (n.userAgentData && n.userAgentData.platform) || n.platform || '';
        return p + '|' + uaFamily;
    }

    function loadCache() {
        try {
            var raw = localStorage.getItem(CACHE_KEY);
            if (!raw) return null;
            var c = JSON.parse(raw);
            if (c && c.sig === platformSig() && typeof c.maxVersion === 'number') {
                return { flags: !!c.flags, maxVersion: c.maxVersion, source: 'cache' };
            }
        } catch (_) {}
        return null;
    }
    function saveCache(c) {
        try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({
                flags: c.flags, maxVersion: c.maxVersion, sig: platformSig()
            }));
        } catch (_) {}
    }

    function runProbe() {
        // Sanity gate: every colour-emoji platform renders 😀. If even that reads
        // monochrome, canvas readback is blocked/poisoned (anti-fingerprinting) —
        // don't trust ANY probe; assume the native font is fine (no regression).
        var v1 = probeColour(LADDER[0].ch);
        if (v1 !== true) {
            return { flags: true, maxVersion: Infinity, source: 'assume-native' };
        }
        var maxVersion = 1;
        for (var i = 1; i < LADDER.length; i++) {
            if (probeColour(LADDER[i].ch) === true && LADDER[i].v > maxVersion) {
                maxVersion = LADDER[i].v;
            }
        }
        var flags = probeColour(FLAG_PROBE) === true;
        return { flags: flags, maxVersion: maxVersion, source: 'probe' };
    }

    function ready() {
        if (readyPromise) return readyPromise;
        var cached = loadCache();
        if (cached) {
            caps = cached;
            readyPromise = Promise.resolve(caps);
            return readyPromise;
        }
        readyPromise = new Promise(function (resolve) {
            var go = function () {
                var c = runProbe();
                caps = c;
                if (c.source === 'probe') saveCache(c); // don't cache the canvas-blocked assumption
                resolve(c);
            };
            // Off the critical path so first paint is never blocked.
            if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 1200 });
            else setTimeout(go, 0);
        });
        return readyPromise;
    }

    function capsSync() { return caps; }

    function reprobe() {
        readyPromise = null; caps = null;
        try { localStorage.removeItem(CACHE_KEY); } catch (_) {}
        return ready();
    }

    // ── Decision + painting ─────────────────────────────────────────────────────
    function hexOf(em) { return (em && (em.h || em.hex) || '').toString(); }
    function verOf(em) { var v = em && (em.v != null ? em.v : em.version); return typeof v === 'number' ? v : 0; }

    // Which vendor pack(s) heal this emoji in 'auto' mode — or null if the native
    // glyph is fine. Returns an ordered fallback CHAIN: a gap in the first pack
    // degrades to the next, then to native text. Reasons are disjoint (a flag is
    // never a "new" glyph and vice-versa) so the chain is unambiguous:
    //   • country/subdivision flags → Twemoji, then OpenMoji (Noto ships no flag
    //     files; this backstops the handful of flags Twemoji omits, e.g. "CQ")
    //   • emoji newer than the font → Noto (only pack with v16/v17 colour)
    function healPacks(em) {
        if (!caps) return null;                              // unknown yet → native (re-decided on ready)
        if (caps.maxVersion === Infinity && caps.flags) return null;
        var hex = hexOf(em);
        if (caps.flags === false && isRegionalFlag(hex)) return [HEAL_FLAG, 'openmoji'];
        if (verOf(em) > caps.maxVersion) return [HEAL_NEW];
        return null;
    }
    function healStyleFor(em) { var p = healPacks(em); return p ? p[0] : null; }
    function needsImage(em) { return healPacks(em) !== null; }

    function imgUrl(hex, style) {
        var slug = String(hex).toLowerCase().replace(/-fe0f$/i, '');
        var pack = style || HEAL_NEW;
        return PACK_BASE() + '/' + pack + '/' + slug + '.' + (PACK_EXT[pack] || 'svg');
    }
    function mirrorUrl(char, pack) {
        var t = PACK_MIRROR[pack];
        if (!t || !char) return null;
        return t.replace('{cp}', hexOfChar(char).toLowerCase());
    }
    // Every address to try for a chain of packs: own copy, then its mirror.
    function urlChain(hex, char, packs) {
        var out = [];
        for (var i = 0; i < packs.length; i++) {
            var m = mirrorUrl(char, packs[i]);
            if (!(m && packDown[packs[i]])) out.push({ pack: packs[i], url: imgUrl(hex, packs[i]), own: true });
            if (m) out.push({ pack: packs[i], url: m, own: false });
        }
        return out;
    }
    // The mirror answered after the own copy failed → the own pack is not on this origin.
    function noteMirrorWin(chain, i) {
        var c = chain[i];
        if (c && !c.own && i > 0 && chain[i - 1].own && chain[i - 1].pack === c.pack) packDown[c.pack] = true;
    }

    // The chain to walk for an explicit vendor style: that pack first, then the
    // heal packs for this device, then native text. Without the tail, every glyph
    // the chosen pack lacks would fall straight to a native glyph the device may
    // not be able to draw either — i.e. a tofu box in an exported thumbnail.
    function explicitChain(style, em) {
        var tail = (PACK_NEXT[style] || []).concat(healPacks(em) || []);
        var chain = [style];
        for (var i = 0; i < tail.length; i++) if (tail[i] !== style) chain.push(tail[i]);
        return chain;
    }

    // Fill `el` with the emoji. Returns 'native' or 'image'.
    //   style 'auto' (default): native, auto-healed to an SVG only where this
    //                           device can't draw the glyph (flags / new emoji).
    //   style 'native'        : always the native glyph (no heal).
    //   style twemoji/noto/openmoji/fluent3d: always that pack, falling back to
    //                           the heal chain and then native for the glyphs it lacks.
    function paint(el, em, opts) {
        opts = opts || {};
        var style = opts.style || 'auto';
        var hex = hexOf(em);
        var char = opts.char || (em && (em.e || em.char)) || '';
        var label = opts.label || '';
        var fallback = opts.fallbackChar != null ? opts.fallbackChar : char;

        var packs;
        if (style === 'native') packs = null;
        else if (style === 'auto') packs = healPacks(em);
        else packs = explicitChain(style, em); // explicit vendor pack, then heal, then native

        // Clear current content.
        while (el.firstChild) el.removeChild(el.firstChild);

        if (!packs || !packs.length) {
            el.textContent = char;
            return 'native';
        }
        var img = document.createElement('img');
        img.className = 'em-img';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.draggable = false;
        if (label) img.alt = label;
        var chain = urlChain(hex, char, packs);
        var i = 0;
        img.src = chain[i].url;
        img.onload = function () { noteMirrorWin(chain, i); };
        img.onerror = function () {
            // Try the next address in the chain; if exhausted, degrade to native text.
            if (++i < chain.length) { this.src = chain[i].url; return; }
            this.onerror = null;
            var t = document.createTextNode(fallback);
            if (this.parentNode) this.parentNode.replaceChild(t, this);
        };
        el.appendChild(img);
        return 'image';
    }

    // ── Canvas bridge ───────────────────────────────────────────────────────────
    // paint() fills a DOM element. A canvas caller (Konva layer, exported PNG)
    // needs the same decision delivered as an image object it can draw.
    //
    // Why the canvas case is the harsher one: a tofu box in a picker grid is a
    // cell the user simply doesn't click. A tofu box on a canvas is baked into
    // the PNG they download and publish, and nothing on screen says it happened.

    // Codepoints of a glyph → emojibase hex ("1F44D-1F3FD").
    // Equivalent to the catalogue's own `h` field: verified against all 1940 rows
    // of base.json — 0 mismatches once each codepoint is padded to 4 digits and
    // a trailing FE0F is normalised away (imgUrl already strips that). The padding
    // is not cosmetic: 14 rows (copyright, registered, the keycaps) are below
    // U+1000 and emojibase writes them "00A9" / "0023-FE0F-20E3".
    //
    // This exists so a caller holding only the character — a saved document, a
    // template, a legacy model row — can still ask the heal question.
    function hexOfChar(ch) {
        return Array.from(String(ch || ''))
            .map(function (c) { return c.codePointAt(0).toString(16).toUpperCase().padStart(4, '0'); })
            .join('-');
    }

    // Decode size, and it has to be OURS. Pack SVGs carry a viewBox and no
    // width/height (checked across the three SVG packs: noto 128, twemoji 36,
    // openmoji 72), and an <img> whose source has no intrinsic size draws
    // inconsistently — Firefox has historically reported 0x0. It is also the
    // sharpness ceiling: an SVG <img> is rasterised ONCE at this size and scaled
    // from there, so a canvas caller exporting at pixelRatio 3 should ask for the
    // size it will draw. ⚠️ For fluent3d the ceiling is the ART, not this number:
    // those are 256px PNGs, so asking for more than 256 upscales a raster.
    var IMG_PX = 160;
    var imgCache = new Map();   // url|px → Promise<HTMLImageElement>

    // Name predates the PNG pack; it loads whatever imgUrl() produced.
    function loadSvg(url, px) {
        var key = url + '|' + px;
        var hit = imgCache.get(key);
        if (hit) return hit;
        var pr = new Promise(function (resolve, reject) {
            var img = new Image();
            img.width = px; img.height = px;
            img.decoding = 'async';
            // Same-origin (/vendor/emoji/...), so the canvas it lands on stays clean
            // and toDataURL()/toBlob() keep working. Never point this at a CDN
            // without crossOrigin — a tainted canvas fails the export, not the draw.
            // The pack mirror (jsDelivr, ACAO *) is asked WITH it for that reason.
            if (/^https?:/i.test(url)) img.crossOrigin = 'anonymous';
            img.onload = function () { resolve(img); };
            img.onerror = function () { reject(new Error('emoji svg 404: ' + url)); };
            img.src = url;
        });
        imgCache.set(key, pr);
        return pr;
    }

    // Resolve to a drawable <img> for `em`, or null when the native glyph is fine.
    //   em      {h|hex, v|version} — hexOfChar() builds the minimum viable one
    //   opts    {px} decode size, default 160
    //           {style} explicit vendor pack ('fluent3d', 'noto', …). WITHOUT it
    //           this resolves an image only where the device needs healing, so a
    //           canvas caller offering a style picker MUST pass it — otherwise
    //           every glyph the device can draw comes back null and silently
    //           renders as the platform font instead of the chosen style.
    //   returns Promise<{img, style, hex} | null>
    // Walks the same pack chain as paint(): a 404 in the first pack falls through
    // to the next, and an exhausted chain resolves null (caller draws native text,
    // which is the honest outcome — we have nothing better than the OS glyph).
    function imageFor(em, opts) {
        var px = Math.max(16, Math.min(1024, (opts && opts.px) || IMG_PX));
        var want = opts && opts.style;
        return ready().then(function () {
            var packs = (want && want !== 'auto' && want !== 'native')
                ? explicitChain(want, em)
                : healPacks(em);
            if (!packs || !packs.length) return null;
            var hex = hexOf(em);
            var chain = urlChain(hex, em && (em.e || em.char), packs);
            var i = 0;
            var tryNext = function () {
                if (i >= chain.length) return null;
                var k = i++;
                return loadSvg(chain[k].url, px)
                    .then(function (img) { noteMirrorWin(chain, k); return { img: img, style: chain[k].pack, hex: hex }; })
                    .catch(tryNext);
            };
            return tryNext();
        }).catch(function () { return null; });
    }

    window.EmojiRender = {
        ready: ready,
        capsSync: capsSync,
        needsImage: needsImage,
        healStyleFor: healStyleFor,
        imgUrl: imgUrl,
        urlChain: function (em, style) { return urlChain(hexOf(em), em && (em.e || em.char), explicitChain(style, em)).map(function (c) { return c.url; }); },
        hexOfChar: hexOfChar,
        imageFor: imageFor,
        paint: paint,
        isRegionalFlag: isRegionalFlag,
        reprobe: reprobe,
        _ladder: LADDER
    };
})();
